// @flow
import * as React from 'react';
import styled, { css } from 'styled-components';
import * as SS from 'styled-system';
import { Link as ReactRouterLink } from 'components/RouterLink';
import Box from 'components/Box';
import Hide from 'components/Hide';
import TrackedLink from 'components/TrackedLink';
import ClickOutside from 'utils/ClickOutside';
import Icon from 'components/Icon';
import { colors } from '../theme';

const NavLink = styled.a`
  display: inline-flex;
  font-size: inherit;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 1/4;
  }
  ${SS.fontWeight};
  ${props =>
    props.blue
      ? `
    color: ${colors.waterBlue};
    &:hover,
    &:focus {
      color: ${colors.niceBlue};
    }
  `
      : `
    color: #717375;
    &:hover,
    &:focus {
      color: ${colors.waterBlue};
    }
  `}
`;

const RouterNavLink = NavLink.withComponent(ReactRouterLink);

const MenuList = styled.div`
  ${props =>
    props.up &&
    css`
      display: flex;
      flex-direction: column-reverse;
    `};
`;

const StyledDropdownMenuNav = styled(NavLink)`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 11px 12px;
`;

type HideOptions = {
  xs?: boolean,
  sm?: boolean,
  md?: boolean,
  lg?: boolean,
  xl?: boolean
};

const DropdownMenuNav = ({
  open,
  up,
  children,
  hideArrow,
  ...props
}: {
  open: boolean,
  up?: boolean,
  children: React.Node,
  hideArrow?: HideOptions
}) => (
  <StyledDropdownMenuNav {...props}>
    <Box>{children}</Box>
    <Hide {...hideArrow}>
      <Box mt="-2px" ml="5px">
        <Icon
          name="chevron"
          width={9}
          height={5}
          direction={(open && !up) || (!open && up) ? 'down' : 'up'}
          fill="currentColor"
        />
      </Box>
    </Hide>
  </StyledDropdownMenuNav>
);

export const DropdownMenuItem = styled(NavLink)`
  display: block;
  margin: 0;
  max-width: 220px;
  min-width: 100%;
  padding: 10px 12px;
  overflow: hidden;
  cursor: pointer;
  color: ${colors.battleshipGrey};
  text-overflow: ellipsis;
  text-decoration: none;
  font-size: inherit;
  white-space: nowrap;
  font-weight: normal;

  &:hover {
    background-color: #f2eeee;
    text-decoration: none;
    color: ${colors.greyishBrown};
  }

  ${props =>
    props.disabled &&
    css`
      opacity: 1/4;
      color: #484848;
      cursor: not-allowed;
      background-color: #f2eeee;
      text-decoration: none;
    `};
`;

export const DropdownMenuItemRouter = DropdownMenuItem.withComponent(
  RouterNavLink
);

export const TrackedDropdownMenuItem = DropdownMenuItem.withComponent(
  TrackedLink
);

const DropdownMenuContainer = styled.div`
  position: absolute;
  left: -1px;
  z-index: 2000;
  border: solid 1px ${colors.aluminum};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.125);
  background-color: white;
  border-radius: 4px;
  display: ${props => (props.open ? 'block' : 'none')};
  ${props =>
    props.up
      ? css`
          bottom: -1px;
          ${DropdownMenuItem}, ${DropdownMenuItemRouter} {
            border-bottom: solid 1px ${colors.aluminum};
          }
        `
      : css`
          top: -1px;
          ${DropdownMenuItem}, ${DropdownMenuItemRouter} {
            border-top: solid 1px ${colors.aluminum};
          }
        `};
`;

const StyledDropdownMenu = styled.div`
  position: relative;
  ${props =>
    props.right &&
    css`
      margin-left: auto;
    `};
`;

type Props = {
  title: boolean => React.Node,
  right?: boolean,
  up?: boolean,
  children: any,
  'data-qa-id': string,
  openQaId?: string,
  closeQaId?: string,
  dropdownQaId?: string,
  'aria-label'?: string,
  onClick?: Function,
  blue?: boolean,
  hideArrow?: HideOptions
};

const DropdownMenu = ({
  title,
  right,
  up,
  children,
  'data-qa-id': qaId,
  openQaId,
  closeQaId,
  dropdownQaId,
  'aria-label': ariaLabel,
  onClick,
  blue,
  hideArrow,
  ...props
}: Props) => {
  const [open, setOpen] = React.useState(false);

  const toggleMenu = (ev: Event) => {
    setOpen(!open);
    ev.preventDefault();
  };

  const closeMenu = () => {
    setOpen(false);
  };

  const dropdownNavClick = e => {
    toggleMenu(e);
    if (onClick) {
      onClick(e);
    }
  };

  const DropdownNav = ({ menuState }: { menuState: string }) => (
    <DropdownMenuNav
      href="#"
      onClick={dropdownNavClick}
      open={open}
      up={up}
      data-qa-id={qaId ? `${qaId}-${menuState}` : undefined}
      aria-label={ariaLabel}
      blue={blue}
      hideArrow={hideArrow}
    >
      {title(open)}
    </DropdownMenuNav>
  );

  return (
    <StyledDropdownMenu right={right} {...props}>
      <DropdownNav menuState="open" />
      <DropdownMenuContainer open={open} up={up} data-qa-id={qaId}>
        <ClickOutside onClickOutside={closeMenu}>
          <MenuList up={up}>
            <DropdownNav menuState="close" />
            {children({ closeMenu, open })}
          </MenuList>
        </ClickOutside>
      </DropdownMenuContainer>
    </StyledDropdownMenu>
  );
};

export default DropdownMenu;

export const DropdownMenuSection = styled.div`
  border-top: solid 1px ${colors.aluminum};
  width: 100%;
  padding: 9px 0;
`;

export const DropdownMenuSectionTitle = styled.div`
  color: #b0b4b9;
  font-size: inherit;
  font-style: italic;
  text-decoration: none;
  white-space: nowrap;
  display: block;
  margin: 0;
  padding: 6px 12px;
`;

export const DropdownMenuSectionItem = styled(DropdownMenuItem)`
  border: none;
  padding: 6px 12px;
`;

export const DropdownMenuSectionItemRouter = styled(DropdownMenuItemRouter)`
  border: none;
  padding: 6px 12px;
`;
